import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { NavLink, useNavigate } from "react-router-dom";
import { FileText, BookOpen, Layers, Users, Settings, LogOut, Menu, LayoutDashboard, Building2, KeyRound, UserRound, PanelLeftClose, PanelLeftOpen, Trash2 } from "lucide-react";
import { EssaiBanner } from "@/components/EssaiBanner";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ProfileDialog } from "@/components/ProfileDialog";
import InstallButton from "@/components/InstallButton";
import { useState } from "react";

const linkBase =
  "flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors";

function Item({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `${linkBase} ${isActive ? "bg-dd-blue text-white" : "text-dd-night hover:bg-dd-light/60"}`
      }
    >
      <Icon size={18} strokeWidth={2} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AppLayout({ children }) {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("madlo.sidebar") !== "open");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("madlo.sidebar", c ? "open" : "closed"); return !c; });
  const [pwOpen, setPwOpen] = useState(false);
  const [profilOpen, setProfilOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const exitSupport = async () => {
    await api.post("/auth/support-exit");
    const { data } = await api.get("/auth/me");
    setUser(data);
    navigate("/master");
  };

  const nav = (
    <nav className="flex flex-col gap-1 px-3" onClick={() => setOpen(false)}>
      {user?.role === "superadmin" ? (
        <Item to="/master" icon={Building2} label="Back-office MADLO" testid="nav-master" />
      ) : (
        <>
          <Item to="/tableau-de-bord" icon={LayoutDashboard} label="Tableau de bord" testid="nav-dashboard" />
          <Item to="/constats" icon={FileText} label="Constats" testid="nav-constats" />
          <Item to="/cles" icon={KeyRound} label="Remise de clés" testid="nav-cles" />
          <Item to="/corbeille" icon={Trash2} label="Corbeille" testid="nav-corbeille" />
        </>
      )}
      {user?.role === "admin" && (
        <>
          <div className="mt-6 mb-2 px-4 text-xs uppercase tracking-wider text-dd-warm">Administration</div>
          <Item to="/admin/catalogue" icon={BookOpen} label="Catalogue" testid="nav-catalogue" />
          <Item to="/admin/typologies" icon={Layers} label="Typologies" testid="nav-typologies" />
          <Item to="/admin/utilisateurs" icon={Users} label="Utilisateurs" testid="nav-users" />
          <Item to="/admin/reglages" icon={Settings} label="Réglages" testid="nav-settings" />
        </>
      )}
    </nav>
  );

  const sidebar = (drawer) => (
    <aside className={`flex flex-col w-64 bg-white border-r border-dd-light shrink-0 ${drawer ? "fixed inset-y-0 left-0 z-50 shadow-2xl" : ""}`} onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-6 border-b border-dd-light flex items-center justify-between gap-2">
        <BrandLogo className="h-12" />
        <button data-testid="sidebar-collapse-btn" onClick={() => { setOpen(false); if (!drawer) toggleCollapsed(); }} title="Masquer le menu"
          className="h-9 w-9 inline-flex items-center justify-center rounded-md text-dd-blue hover:bg-dd-light/60">
          <PanelLeftClose size={18} />
        </button>
      </div>
      <div className="flex-1 py-4 overflow-y-auto">{nav}</div>
      <div className="p-3 border-t border-dd-light">
        <div className="px-4 py-2 text-sm">
          <div className="font-semibold text-dd-blue truncate">{user?.name}</div>
          <div className="text-xs text-dd-warm">{user?.role === "superadmin" ? "Éditeur MADLO" : user?.role === "admin" ? "Administrateur" : "Gérant"}</div>
        </div>
        {!user?.support_readonly && (
          <>
            <button data-testid="profile-btn" onClick={() => { setOpen(false); setProfilOpen(true); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium text-dd-night hover:bg-dd-light/60">
              <UserRound size={18} /> Mon profil
            </button>
            <button data-testid="change-password-btn" onClick={() => { setOpen(false); setPwOpen(true); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium text-dd-night hover:bg-dd-light/60">
              <KeyRound size={18} /> Mot de passe
            </button>
          </>
        )}
        <button data-testid="logout-btn" onClick={onLogout}
          className="mt-1 w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium text-dd-night hover:bg-dd-light/60">
          <LogOut size={18} /> Déconnexion
        </button>
        <InstallButton className="mt-1" />
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-dd-light/30">
      {/* Sidebar fixe (desktop, non repliée) */}
      {!collapsed && <div className="hidden md:flex">{sidebar(false)}</div>}

      {/* Burger (mobile toujours, desktop quand repliée) */}
      <button data-testid="burger-menu-btn" onClick={() => setOpen(true)} title="Menu"
        className={`fixed top-3 left-3 z-40 h-11 w-11 items-center justify-center rounded-md bg-white border border-dd-light shadow-sm text-dd-blue hover:bg-dd-light/60 ${collapsed ? "flex" : "flex md:hidden"}`}>
        <Menu size={20} />
      </button>

      {/* Tiroir de navigation */}
      {open && (
        <div className="fixed inset-0 z-50 bg-dd-night/40" onClick={() => setOpen(false)} data-testid="nav-drawer">
          {sidebar(true)}
          {collapsed && (
            <button data-testid="sidebar-pin-btn" onClick={(e) => { e.stopPropagation(); toggleCollapsed(); setOpen(false); }}
              className="hidden md:flex fixed bottom-4 left-4 z-[60] h-10 items-center gap-2 px-3 rounded-md bg-dd-blue text-white text-sm shadow-lg">
              <PanelLeftOpen size={16} /> Garder le menu affiché
            </button>
          )}
        </div>
      )}

      <main className="flex-1 min-w-0">
        <EssaiBanner acces={user?.acces} isAdmin={user?.role === "admin"} support={user?.support} onExitSupport={exitSupport} />
        <div className={`p-4 md:p-8 max-w-[1400px] ${collapsed ? "pl-16 md:pl-20" : "pl-16 md:pl-8"}`}>{children}</div>
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <ProfileDialog open={profilOpen} onOpenChange={setProfilOpen} />
    </div>
  );
}
